import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import {
  sendMessage as sendMessageAPI,
  getThreads as getThreadsAPI,
  getConversation as getConversationAPI,
  markMessageRead as markMessageReadAPI,
  getThreadsWithUnreadMessages as getThreadsWithUnreadMessagesAPI,
  outboundAriConfig,
  disableAri as disableAriApi,
  enableAri as enableAriApi,
} from 'util/Api';

export const updateUnreadMsgs = createAction('thread/updateUnreadMsgs');
export const realtimeMsg = createAction('thread/realtimeMsg');
export const updateUnreadThreads = createAction('thread/updateUnreadThreads');
export const updateArchivedThread = createAction('thread/updateArchivedThread');
export const resetHasDuplicates = createAction('thread/resetHasDuplicates');
export const inboxFilterType = createAction('thread/inboxFilterType');
export const selectThreadFromDashboard = createAction('thread/selectThreadFromDashboard');

/**
 * Updates the real-time unread count for a specific inbox type.
 * Math.max(0, count) ensures the count never goes below 0
 */
export const updateUnreadCountRealTime = createAction(
  'thread/updateUnreadCountRealTime',
  (inboxType, count) => ({
    payload: {
      inboxType,
      count: Math.max(0, count),
    },
  }),
);

export const getThreads = createAsyncThunk(
  'thread/getThreads',
  async (
    {
      pageNumber,
      inboxType,
      userName,
      id,
      messageOptions,
      campaignName,
      searchedUserName,
      inboxIdx,
      accountName,
      itemsPerPage,
    },
    { rejectWithValue },
  ) => {
    try {
      return await getThreadsAPI({
        pageNumber,
        inboxType,
        userName,
        id,
        messageOptions,
        campaignName,
        searchedUserName,
        inboxIdx,
        accountName,
        itemsPerPage,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const sendMessage = createAsyncThunk(
  'thread/sendMessage',
  async (
    {
      candidateId,
      text,
      actingUser,
      threadId,
      twilioTemplateId,
      contentVariables,
      selectedChannel,
    },
    { rejectWithValue },
  ) => {
    try {
      return await sendMessageAPI({
        candidateId,
        text,
        actingUser,
        threadId,
        twilioTemplateId,
        contentVariables,
        selectedChannel,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getConversation = createAsyncThunk(
  'thread/getConversation',
  async ({ candidateId, markAsRead, threadId, actingUser }, { rejectWithValue }) => {
    try {
      return await getConversationAPI({
        candidateId,
        markAsRead,
        threadId,
        actingUser,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const markMessageRead = createAsyncThunk(
  'thread/markAsRead',
  async ({ messageId }, { rejectWithValue }) => {
    try {
      return await markMessageReadAPI({ messageId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getThreadsWithUnreadMessages = createAsyncThunk(
  'thread/getThreadsWithUnreadMessages',
  async ({ inboxType, username, page, itemsPerPage }, { rejectWithValue }) => {
    try {
      return await getThreadsWithUnreadMessagesAPI({
        inboxType,
        username,
        page,
        itemsPerPage,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const enableAri = createAsyncThunk(
  'ari/enableAri',
  async (
    { threadId, candidateId, sequenceId, text, initialMessageOverride },
    { rejectWithValue },
  ) => {
    try {
      return await enableAriApi({
        threadId,
        candidateId,
        sequenceId,
        text,
        initialMessageOverride,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const disableAri = createAsyncThunk(
  'ari/disableAri',
  async ({ threadId }, { rejectWithValue }) => {
    try {
      return await disableAriApi({ threadId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getSequences = createAsyncThunk(
  'ari/getSequences',
  async ({ threadId }, { rejectWithValue }) => {
    try {
      return await outboundAriConfig({ threadId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);
